<template>
  <div>
    <template v-if="$apollo.loading">
      <loading/>
    </template>

    <template v-else>
      <div class="columns is-multiline is-variable is-4">
        <div class="column is-3-desktop is-4-tablet">
          <div class="columns is-multiline is-gapless">
            <div class="column is-12">
              <album-cover v-bind:album="this.album" class="is-square">
                <span class="tag album-audio-format-tag is-dark is-light is-overlay is-uppercase has-text-weight-semibold">
                  {{ albumAudioFormat }}
                </span>
              </album-cover>
            </div>

            <div class="column is-12 is-hidden-mobile">
              <album-controls
                v-bind:album="album"
                v-bind:status="status"
                v-on:shuffle="playShuffle"
                style="padding-top:0.75rem;"
              />
            </div>
          </div>
        </div>

        <div class="column poly-pt-0-mobile">
          <h2 class="is-size-7 is-uppercase is-hidden-mobile">{{ album.releaseType }}</h2>
          <h1 class="title is-size-2 is-size-5-mobile has-text-centered-mobile has-text-weight-extra-bold">
            <router-link v-bind:to="{ name: 'album', params: { id: album.id } }">
              {{ album.name }}
            </router-link>
          </h1>
          <h2 class="subtitle is-size-4 is-size-6-mobile has-text-grey-light has-text-centered-mobile has-text-weight-semibold">
            <ul class="album-artists">
              <li
                v-for="(artist, index) in album.artists"
                v-bind:key="artist.id"
              >
                <span v-if="index > 0">, </span>
                <router-link v-bind:to="{ name: 'artist', params: { id: artist.id } }" class="has-text-grey-light">{{ artist.name }}</router-link>
              </li>
            </ul>
          </h2>
          <h2 class="title is-size-6 is-size-7-mobile has-text-centered-mobile has-text-weight-normal has-text-grey-light poly-mb-1">
            {{ album.releaseDate }} ·
            {{ album.tracks.length }} tracks ·
            <template v-if="albumDiscsNumber > 1">{{ albumDiscsNumber }} discs ·</template>
            {{ albumDuration }} total ·
            <router-link v-bind:to="{ name: 'genre', params: { id: album.genre.id } }" class="is-capitalized has-text-grey-light">
              {{ album.genre.name }}
            </router-link>
          </h2>

          <table v-bind:class="{ 'is-narrow': showTracksArtist }" class="table is-hoverable is-size-6-mobile is-fullwidth">
            <caption>
              <album-controls
                v-bind:album="album"
                v-bind:status="status"
                v-on:shuffle="playShuffle"
                class="is-hidden-desktop is-hidden-tablet poly-pb-0-5"
              ></album-controls>
            </caption>
            <col width="40">
            <col>
            <col class="poly-hidden-touch" width="60">
            <col width="60">
            <tbody>
              <template v-for="track in album.tracks">
                <album-track-disc-number
                  v-if="track.number === 1 && showAlbumDiscNumber"
                  v-bind:key="track.id"
                  v-bind:discNumber="track.discNumber"
                ></album-track-disc-number>

                <album-track
                  v-bind:key="track.id"
                  v-bind:track="track"
                  v-bind:status="trackStatuses[track.id]"
                  v-bind:showArtist="showTracksArtist"
                  v-on:play="playTrack"
                ></album-track>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
  import { playerStatuses } from '@/constants'

  import Loading from '@/components/Loading'

  import AlbumCover from '@/components/AlbumCover'
  import AlbumControls from '@/components/AlbumControls'
  import AlbumTrack from '@/components/AlbumTrack'
  import AlbumTrackDiscNumber from '@/components/AlbumTrackDiscNumber'

  import shuffleArray from '@/mixins/shuffleArray'

  const formatDuration = require('format-duration')

  export default {
    name: 'Album',
    mixins: [shuffleArray],
    data: function () {
      return {
        status: playerStatuses.STOPPED,
        trackStatuses: {}
      }
    },
    components: {
      Loading,
      AlbumCover,
      AlbumControls,
      AlbumTrack,
      AlbumTrackDiscNumber
    },
    computed: {
      playerStatus: function () {
        return this.$store.state.playerStatus
      },
      currentTrack: function () {
        return this.$store.getters.currentTrack
      },
      albumDuration: function () {
        const albumDuration = this.album.tracks.reduce(
          (accumulator, track) => { return accumulator + track.duration },
          0
        )

        return formatDuration(albumDuration * 1000)
      },
      albumDiscsNumber: function () {
        const trackDiscNumbers = this.album.tracks.map(
          (track) => { return track.discNumber }
        )

        return Math.max(...trackDiscNumbers)
      },
      albumAudioFormat: function () {
        const flacTracks = this.album.tracks.map(
          (track) => track.files.find((file) => file.mimeType === 'audio/x-flac')
        ).filter((track) => Boolean(track))

        if (flacTracks.length === this.album.tracks.length) {
          return 'flac'
        } else {
          return 'mp3'
        }
      },
      showTracksArtist: function () {
        const albumArtistIds = this.album.artists.map(
          (artist) => { return artist.id }
        ).sort().toString()

        return this.album.tracks.some(
          (track) => {
            const trackArtistIds = track.artists.map(
              (artist) => { return artist.id }
            ).sort().toString()

            if (albumArtistIds !== trackArtistIds) { return true }
          }
        )
      },
      showAlbumDiscNumber: function () {
        return this.albumDiscsNumber > 1
      }
    },
    watch: {
      playerStatus: function (newPlayerStatus) {
        if (this.currentTrack && this.currentTrack.album.id === this.album.id) {
          this.trackStatuses[this.currentTrack.id] = newPlayerStatus
          this.status = newPlayerStatus
        }
      },
      currentTrack: function (newCurrentTrack, oldCurrentTrack) {
        if (oldCurrentTrack && oldCurrentTrack.album.id === this.album.id) {
          this.trackStatuses[oldCurrentTrack.id] = playerStatuses.STOPPED
          this.status = playerStatuses.STOPPED
        }

        if (newCurrentTrack && newCurrentTrack.album.id === this.album.id) {
          this.trackStatuses[newCurrentTrack.id] = this.playerStatus
          this.status = this.playerStatus
        }
      }
    },
    methods: {
      playTrack: function (track) {
        const trackIndex = this.album.tracks.findIndex(
          (album_track) => { return album_track.id === track.id }
        )

        this.$store.commit('playTracks', { tracks: this.album.tracks, trackIndex: trackIndex })
      },
      playShuffle: function () {
        var shuffledTracksToPlay = this.shuffleArray(this.album.tracks)

        if (this.currentTrack && this.album.tracks.length > 1) {
          while (shuffledTracksToPlay[0].id === this.currentTrack.id) {
            shuffledTracksToPlay = this.shuffleArray(shuffledTracksToPlay)
          }
        }

        this.$store.commit('playTracks', { tracks: shuffledTracksToPlay })
      }
    },
    apollo: {
      album: {
        query: require('@/graphql/Album.gql'),
        variables () {
          return {
            id: this.$route.params.id
          }
        },
        update: function (data) {
          data.album.tracks.forEach((track) => {
            if (this.currentTrack && this.currentTrack.id === track.id) {
              this.$set(this.trackStatuses, track.id, this.playerStatus)
              this.status = this.playerStatus
            } else {
              this.$set(this.trackStatuses, track.id, playerStatuses.STOPPED)
            }
          })

          return data.album
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "~bulma/sass/utilities/initial-variables";

  table { table-layout: fixed; }

  .album-artists li { display: inline; }

  .album-audio-format-tag {
    width: 16%;
    margin: auto;
    margin-right: 0.1rem;
    margin-bottom: 0.2rem;
    background-color: rgba(54, 54, 54, 0.3) !important;
    border: 1px solid #fff;
    transform: scale(0.7);
    backdrop-filter: blur(3px);
  }

  // Mobile specific modifiers
  @media (max-width: $tablet - 1) {
    .album-audio-format-tag {
      width: 14%;
      margin-right: auto;
      transform: scale(0.7);
    }
  }
</style>
